/**
 * 10.8 — SAVE NA NUVEM (Fase 1 do servidor/PvP). Camada FINA por cima do save local:
 *
 *  · Identidade: login ANÔNIMO no Supabase (persistido) → cada aparelho tem um id estável,
 *    sem precisar criar conta. (Depois dá pra "promover" pra e-mail/OAuth e ficar portátil.)
 *  · No BOOT (antes do Game carregar): puxa o save da nuvem; se estiver à frente do local,
 *    grava no localStorage pra o jogo carregar a versão certa (restaurar em outro aparelho).
 *  · A cada save local: empurra o blob pra nuvem (debounced). É um decorator do SaveStorage.
 *
 * Tudo é TOLERANTE a falha: sem internet / tabela ausente / login anônimo desligado → o jogo
 * segue 100% no save local (nunca trava o boot). O real é o local; a nuvem é espelho/backup.
 *
 * Requer no painel do Supabase: (1) "Anonymous sign-ins" LIGADO; (2) a tabela `saves` + RLS.
 */
import type { SaveStorage } from "../save/Storage";
import { supabase } from "./supabase";
import { GameConfig } from "../config/GameConfig";

const TABLE = "saves";
const REV_KEY = "vh_cloud_rev"; // localStorage: última revisão que este aparelho conhece

let userId: string | null = null;
let firstPushToasted = false;

function localRev(): number {
  try {
    return parseInt(localStorage.getItem(REV_KEY) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}
function setLocalRev(r: number): void {
  try {
    localStorage.setItem(REV_KEY, String(r));
  } catch {
    /* ignora */
  }
}
function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * 10.8D — "AVANÇO" de um save (monotônico: só cresce). Usado pra decidir a restauração sem
 * NUNCA perder progresso: nível e profundidade nunca caem, e a absorção é permanente. Um save
 * da nuvem só substitui o local se for MAIS avançado — fim do "toda atualização zera tudo".
 * -1 = sem save; 0 = JSON inválido (perde pra qualquer save real, mas ganha de "sem save").
 */
function scoreOne(json: string): number {
  try {
    const s = JSON.parse(json) as { level?: unknown; progress?: Record<string, unknown>; inventory?: { bag?: unknown } }; // 10.9GM
    const p = s.progress ?? {};
    const level = Number(s.level) || 0;
    const depth = Number(p.depth) || 1;
    const absorb = (Number(p.absorbHp) || 0) + (Number(p.absorbPower) || 0);
    const elites = Number(p.elitesDefeated) || 0;
    // 10.9FK — BUG "perdi TODAS as armas ao voltar": o avanco NAO contava o equipamento, entao um
    // save da nuvem com o MESMO nivel porem SEM as armas empatava/ganhava e sobrescrevia o local,
    // apagando os itens. Agora conta os itens (quantidade + nivel + raridade) num peso que quebra o
    // empate por nivel mas nunca supera uma diferenca real de nivel — save COM armas nao perde pra um sem.
    // 10.9GM — o "gear" precisa ser MONOTÔNICO (vender/quebrar/morrer não pode derrubar o avanço,
    // senão um save velho cheio de itens vencia uma sessão legítima que vendeu/forjou). Usa o ÍNDICE
    // (itens já descobertos: só cresce) + um pequeno peso da bolsa só pra desempate.
    const disc = Array.isArray(p.discoveredItems) ? (p.discoveredItems as unknown[]).length : 0;
    const bagN = s.inventory && Array.isArray(s.inventory.bag) ? (s.inventory.bag as unknown[]).length : 0;
    const gear = Math.min(9000, disc * 40 + Math.min(bagN, 40)); // teto ~0,9 nivel: nunca engole um nivel
    return depth * 1e9 + level * 1e6 + gear * 100 + absorb * 10 + elites;
  } catch {
    return 0;
  }
}
/** 10.9FK — quantos ITENS (bolsa + equipados) o save tem, somando todos os personagens. */
function gearCount(json: string | null): number {
  if (!json) return 0;
  try {
    const o = JSON.parse(json) as { __mc?: number; chars?: Record<string, string> };
    const countOne = (j: string): number => {
      try { const s = JSON.parse(j) as { inventory?: { bag?: unknown[] } }; return Array.isArray(s.inventory?.bag) ? s.inventory!.bag!.length : 0; } catch { return 0; }
    };
    if (o && o.__mc === 2 && o.chars && typeof o.chars === "object") {
      let n = 0; for (const k of Object.keys(o.chars)) n += countOne(o.chars[k]); return n;
    }
    return countOne(json);
  } catch { return 0; }
}

/**
 * 10.9GM — Funde dois blobs multi-personagem: pra cada classe, fica a versão com maior avanço
 * (scoreOne). Devolve null se algum dos dois não for um blob válido do token atual (aí vale a
 * comparação antiga do blob inteiro). Devolve a string do `local` intacta quando nada muda.
 */
function mergeChars(local: string | null, cloud: string): string | null {
  if (!local) return null;
  try {
    type Blob = { __mc?: number; wipe?: number; activeClass?: string | null; chars?: Record<string, string> };
    const a = JSON.parse(local) as Blob;
    const b = JSON.parse(cloud) as Blob;
    const ok = (o: Blob): boolean => !!o && o.__mc === 2 && !!o.chars && typeof o.chars === "object" && (o.wipe ?? 0) === GameConfig.save.wipeToken;
    if (!ok(a) || !ok(b)) return null;
    const out: Record<string, string> = {};
    let changed = false;
    for (const k of new Set([...Object.keys(a.chars!), ...Object.keys(b.chars!)])) {
      const la = a.chars![k], lb = b.chars![k];
      if (la === undefined) { out[k] = lb; changed = true; continue; }
      if (lb === undefined) { out[k] = la; continue; }
      const sa = scoreOne(la), sb = scoreOne(lb);
      // empate: fica o LOCAL (evita restaurar por cima do que o jogador acabou de fazer)
      if (sb > sa) { out[k] = lb; changed = true; } else out[k] = la;
    }
    if (!changed) return local;
    return JSON.stringify({ __mc: 2, wipe: GameConfig.save.wipeToken, activeClass: a.activeClass ?? b.activeClass ?? null, chars: out });
  } catch { return null; }
}

function progressScore(json: string | null): number {
  if (!json) return -1;
  try {
    const o = JSON.parse(json) as { __mc?: number; wipe?: number; chars?: Record<string, string> };
    // 10.9EQ — save de ANTES do ultimo reset geral nao vale NADA. Sem isso, no primeiro minuto apos
    // um reset (antes do save novo subir) o save velho da nuvem "ganhava" por estar mais avancado,
    // era restaurado por cima do progresso novo e so depois descartado — o jogador perdia o que fez.
    if (!o || o.__mc !== 2 || (o.wipe ?? 0) !== GameConfig.save.wipeToken) return -1;
    // 10.9X — blob multi-personagem: soma o avanço de TODOS os personagens (mais chars/progresso = ganha)
    if (o && o.__mc === 2 && o.chars && typeof o.chars === "object") {
      let total = 0;
      for (const k of Object.keys(o.chars)) total += scoreOne(o.chars[k]);
      return total;
    }
    return scoreOne(json); // formato flat legado
  } catch {
    return 0;
  }
}

/** Garante uma sessão (reusa a persistida; senão faz login anônimo). null se indisponível. */
async function ensureAuth(): Promise<string | null> {
  if (userId) return userId;
  try {
    const { data: sess } = await supabase.auth.getSession();
    if (sess.session?.user) {
      userId = sess.session.user.id;
      return userId;
    }
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) {
      console.warn("[cloud] login anônimo indisponível:", error.message, "(ligue 'Anonymous sign-ins' no Supabase)");
      return null;
    }
    userId = data.user?.id ?? null;
    return userId;
  } catch (e) {
    console.warn("[cloud] auth exceção:", e);
    return null;
  }
}

/**
 * 10.9EP — SAVE COMPRIMIDO. O save subia inteiro em texto (~20 KB, 26 KB com o envelope): com
 * gzip vira ~4 KB (medido: 20,5 KB -> 4,1 KB, 5x). Vai na MESMA coluna de texto do Supabase com o
 * prefixo "gz1:" + base64 — nao precisa mexer no banco. Quem le entende OS DOIS formatos: save
 * antigo (JSON puro) continua abrindo. Sem CompressionStream no aparelho -> sobe o JSON puro.
 */
const GZ = "gz1:";
async function pack(raw: string): Promise<string> {
  if (typeof CompressionStream === "undefined") return raw;
  try {
    const cs = new CompressionStream("gzip");
    const w = cs.writable.getWriter();
    void w.write(new TextEncoder().encode(raw));
    void w.close();
    const bytes = new Uint8Array(await new Response(cs.readable).arrayBuffer());
    let bin = "";
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return GZ + btoa(bin);
  } catch {
    return raw;
  }
}
/** Abre o save da nuvem nos dois formatos. null = comprimido mas o aparelho nao consegue abrir. */
/** 10.9IK — exportado pra pagina de ADMIN ler o mesmo blob, sem duplicar o formato. */
export async function unpack(stored: string): Promise<string | null> {
  if (!stored.startsWith(GZ)) return stored; // formato antigo (JSON puro)
  if (typeof DecompressionStream === "undefined") return null;
  try {
    const bin = atob(stored.slice(GZ.length));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const ds = new DecompressionStream("gzip");
    const w = ds.writable.getWriter();
    void w.write(bytes);
    void w.close();
    return await new Response(ds.readable).text();
  } catch {
    return null;
  }
}

function race<T>(p: PromiseLike<T>, ms: number): Promise<T | "timeout"> {
  return Promise.race([p, new Promise<"timeout">((res) => setTimeout(() => res("timeout"), ms))]);
}

/** 10.9IS — relatorio de TRAVAMENTO pra nuvem. Fire-and-forget: nunca lanca, nunca bloqueia, e se a
 *  tabela `crash_reports` nao existir o erro e engolido. E o unico jeito de ler o stack de um
 *  travamento que so acontece no celular do jogador. */
export function reportCrash(p: { onde: string; msg: string; stack: string; state: string; region: string; ua: string; trail?: string }): void {
  try {
    void supabase
      .from("crash_reports")
      .insert({ user_id: userId, onde: p.onde, msg: p.msg.slice(0, 300), stack: p.stack.slice(0, 1200), state: p.state, region: p.region, ua: p.ua.slice(0, 300), trail: (p.trail ?? "").slice(0, 4000) })
      .then(() => undefined, () => undefined);
  } catch { /* nunca */ }
}

export type BootSyncResult = "cloud-restored" | "local-ahead" | "no-cloud" | "offline";

/**
 * Chamado no BOOT, ANTES de o Game ler o save. Faz auth + pull; se a nuvem estiver à frente
 * (ou o aparelho não tiver save local), grava o save da nuvem no localStorage.
 */
export async function cloudBootSync(saveKey: string, timeoutMs = 4500): Promise<BootSyncResult> {
  const uid = await race(ensureAuth(), timeoutMs);
  if (uid === "timeout" || !uid) return "offline";
  try {
    const res = await race(supabase.from(TABLE).select("data, rev").eq("user_id", uid).maybeSingle(), timeoutMs);
    if (res === "timeout") return "offline";
    const { data, error } = res;
    if (error) {
      console.warn("[cloud] pull erro:", error.message, "(rode o SQL da tabela `saves` no Supabase)");
      return "offline";
    }
    const local = readLocal(saveKey);
    if (!data || typeof data.data !== "string" || data.data.length === 0) return local ? "local-ahead" : "no-cloud";
    const cloudRev = typeof data.rev === "number" ? data.rev : 0;
    const cloudRaw = await unpack(data.data); // 10.9EP — comprimido ou JSON puro
    if (cloudRaw === null) return local ? "local-ahead" : "offline"; // aparelho nao abre gzip: nao arrisca
    // 10.8D — decide pela ADVANCEMENT, não pela rev: a nuvem só substitui o local se for MAIS
    // avançada. Impede que uma atualização/relogin restaure um save antigo por cima do progresso.
    const localScore = progressScore(local);
    const cloudScore = progressScore(cloudRaw);
    // 10.9FK — PROTEÇÃO DE EQUIPAMENTO: uma nuvem SEM nenhum item nunca substitui um local COM itens
    // (mesmo que a nuvem esteja alguns níveis à frente). Perder as armas é pior que atrasar 1 nível;
    // além disso, um save sem itens é quase sempre um snapshot velho/regredido. Sobe o local e mantém.
    if (cloudScore > localScore && gearCount(cloudRaw) === 0 && gearCount(local) > 0) {
      cloudPush(saveKey);
      return "local-ahead";
    }
    // 10.9GM — FUSÃO POR PERSONAGEM: antes comparava a SOMA dos personagens e restaurava o blob
    // inteiro — dois personagens fracos "venciam" um forte e o outro personagem SUMIA. Agora cada
    // classe fica com a versão mais avançada (local ou nuvem), e nenhum personagem se perde.
    const merged = mergeChars(local, cloudRaw);
    if (merged !== null) {
      if (merged !== local) {
        try { localStorage.setItem(saveKey, merged); } catch { /* ignora */ }
        setLocalRev(cloudRev);
        toast("☁ Progresso restaurado da nuvem");
        if (merged !== cloudRaw) cloudPush(saveKey); // a nuvem também ganha o que só o local tinha
        return "cloud-restored";
      }
      if (progressScore(local) > cloudScore) cloudPush(saveKey);
      return "local-ahead";
    }
    if (cloudScore > localScore) {
      try {
        localStorage.setItem(saveKey, cloudRaw);
      } catch {
        /* ignora */
      }
      setLocalRev(cloudRev);
      toast("☁ Progresso restaurado da nuvem");
      return "cloud-restored";
    }
    // local igual/mais avançado: mantém e sobe pra nuvem (garante que a nuvem não fique atrás do real)
    if (localScore > cloudScore) cloudPush(saveKey);
    return "local-ahead";
  } catch (e) {
    console.warn("[cloud] bootSync exceção:", e);
    return "offline";
  }
}

let pushTimer: number | undefined;
let pushAt = 0;
let lastPushed = "";
let lastSaveKey = "";
let lastPushAt = 0;
let resyncing = false; // 10.9GL — evita resync em loop
let priority = false;

/**
 * 10.9EP — RITMO DO ENVIO. Antes: todo save local (quase 1 por segundo caçando) reiniciava um
 * atraso de 2,5s -> medido 1 envio a cada ~15s. Com 10 mil jogando isso daria ~667 gravacoes/s.
 * Agora:
 *   - no maximo 1 envio a cada PUSH_EVERY (90s) — o save LOCAL continua a cada evento;
 *   - MOMENTO IMPORTANTE (subir de nivel, compra, boss, raid) adianta pra PRIORITY_EVERY (15s);
 *   - app indo pro fundo/fechando: envia NA HORA (como antes).
 * Um envio agendado nao e empurrado pra frente por novos saves (nao "morre de fome"): quando sai,
 * leva o save mais recente.
 */
const PUSH_EVERY = 90_000;
const PRIORITY_EVERY = 15_000;
const DEBOUNCE = 2_500;

function schedule(): void {
  if (!lastSaveKey) return;
  const now = Date.now();
  const at = Math.max(now + DEBOUNCE, lastPushAt + (priority ? PRIORITY_EVERY : PUSH_EVERY));
  if (pushTimer !== undefined) {
    if (pushAt <= at) return; // ja tem um envio marcado antes disso
    clearTimeout(pushTimer);
  }
  pushAt = at;
  pushTimer = window.setTimeout(() => {
    pushTimer = undefined;
    priority = false;
    void doPush(lastSaveKey);
  }, at - now);
}

/** Save local mudou: agenda o espelho na nuvem no ritmo normal. */
export function cloudPush(saveKey: string): void {
  lastSaveKey = saveKey;
  schedule();
}

/** 10.9EP — momento importante (nivel, compra, boss, raid): a nuvem acompanha mais rapido. */
export function cloudPriority(): void {
  priority = true;
  schedule();
}

// 10.8D — FLUSH ao minimizar/fechar: envia o último save na hora (sem esperar o debounce),
// pra a nuvem nunca ficar atrás do progresso real quando o app vai pro fundo ou fecha.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && lastSaveKey) {
      if (pushTimer !== undefined) {
        clearTimeout(pushTimer);
        pushTimer = undefined;
      }
      void doPush(lastSaveKey);
    }
  });
}

/** 10.9K7 — conta sendo ZERADA: nada mais sobe pra nuvem até a página recarregar. */
let wiping = false;

/**
 * 10.9K7 — ZERAR A CONTA (ordem do botão "Zerar" do painel, via concessão 'wipe'). Apaga TODOS os
 * personagens deste aparelho e grava um blob VAZIO na nuvem por cima (o painel já apaga a linha,
 * mas o aparelho pode ter mandado o save de volta antes de receber a ordem). Quem chama recarrega
 * a página logo depois. Sem rede: o local já foi zerado; a nuvem é sobrescrita no próximo envio.
 */
export async function wipeAccount(saveKey: string): Promise<void> {
  wiping = true;
  if (pushTimer !== undefined) { clearTimeout(pushTimer); pushTimer = undefined; }
  const empty = JSON.stringify({ __mc: 2, wipe: GameConfig.save.wipeToken, activeClass: null, chars: {} });
  try { localStorage.setItem(saveKey, empty); } catch { /* ignora */ }
  try {
    const uid = userId ?? (await ensureAuth());
    if (!uid) return;
    const cur = await supabase.from(TABLE).select("rev").eq("user_id", uid).maybeSingle();
    const rev = Math.max(localRev(), typeof cur.data?.rev === "number" ? cur.data.rev : 0) + 1;
    const { error } = await supabase.from(TABLE).upsert({ user_id: uid, data: await pack(empty), rev, updated_at: new Date().toISOString() });
    if (!error) { setLocalRev(rev); lastPushed = empty; }
  } catch { /* sem rede: o local já está zerado */ }
}

async function doPush(saveKey: string): Promise<void> {
  if (wiping) return;
  try {
    const uid = userId ?? (await ensureAuth());
    if (!uid) return;
    const raw = readLocal(saveKey);
    if (!raw || raw === lastPushed) return;
    lastPushAt = Date.now(); // 10.9EP — conta a tentativa (sem rede nao vira metralhadora)
    const rev = localRev() + 1;
    const packed = await pack(raw); // 10.9EP — ~5x menor
    const { error } = await supabase.from(TABLE).upsert({ user_id: uid, data: packed, rev, updated_at: new Date().toISOString() });
    if (error) {
      console.warn("[cloud] push erro:", error.message);
      // 10.9GL — BUG: conflito de revisão (409 / rev atrasada — outro aparelho/aba subiu antes) só
      // logava e desistia; a rev local nunca avançava e TODO push seguinte falhava igual → o progresso
      // parava de subir pra nuvem. Agora re-sincroniza a rev com a nuvem e tenta de novo, sem
      // sobrescrever uma nuvem que esteja MAIS avançada (aí o boot restaura ela, como sempre).
      if (!resyncing) {
        resyncing = true;
        try {
          const res = await supabase.from(TABLE).select("data, rev").eq("user_id", uid).maybeSingle();
          const cloudRev = typeof res.data?.rev === "number" ? res.data.rev : 0;
          if (cloudRev >= rev) {
            const cloudRaw = typeof res.data?.data === "string" ? await unpack(res.data.data) : null;
            const cloudAhead = cloudRaw !== null && progressScore(cloudRaw) > progressScore(raw);
            setLocalRev(cloudRev);
            if (!cloudAhead) { lastPushed = ""; cloudPush(saveKey); } // rev certa agora: reenvia
            else console.warn("[cloud] nuvem mais avançada — não sobrescrevo (o boot restaura)");
          }
        } catch { /* sem rede: o próximo save tenta de novo */ } finally { resyncing = false; }
      }
      return;
    }
    lastPushed = raw;
    setLocalRev(rev);
    if (!firstPushToasted) {
      firstPushToasted = true;
      toast("☁ Progresso salvo na nuvem");
    }
  } catch (e) {
    console.warn("[cloud] push exceção:", e);
  }
}

/** Decorator do SaveStorage: grava local (idêntico) e agenda o espelho na nuvem. */
export class CloudBackedStorage implements SaveStorage {
  constructor(
    private readonly inner: SaveStorage,
    private readonly saveKey: string,
  ) {}
  load(key: string): string | null {
    return this.inner.load(key);
  }
  save(key: string, value: string): void {
    this.inner.save(key, value);
    if (key === this.saveKey) cloudPush(this.saveKey);
  }
  remove(key: string): void {
    this.inner.remove(key);
  }
}

/** Toast mínimo autocontido (não depende da UI do jogo). Some sozinho. */
function toast(text: string): void {
  try {
    const el = document.createElement("div");
    el.textContent = text;
    el.setAttribute("role", "status");
    el.style.cssText =
      "position:fixed;left:50%;bottom:calc(env(safe-area-inset-bottom,0px) + 18px);transform:translateX(-50%);" +
      "z-index:99999;background:rgba(10,12,26,.92);color:#dbe6ff;border:1px solid rgba(124,77,255,.55);" +
      "border-radius:12px;padding:9px 14px;font:600 13px system-ui,sans-serif;letter-spacing:.02em;" +
      "box-shadow:0 8px 24px rgba(0,0,0,.5),0 0 16px rgba(124,77,255,.25);opacity:0;transition:opacity .25s,transform .25s;" +
      "pointer-events:none;max-width:90vw;text-align:center";
    document.body.appendChild(el);
    requestAnimationFrame(() => {
      el.style.opacity = "1";
      el.style.transform = "translateX(-50%) translateY(-4px)";
    });
    setTimeout(() => {
      el.style.opacity = "0";
      setTimeout(() => el.remove(), 300);
    }, 2600);
  } catch {
    /* ambiente sem DOM: ignora */
  }
}
